import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUploadCostPrices } from '~/hooks/apis/tours/useTourCostPrices';

import { ServiceIdentifier } from '~/types/tours/costPrice';

interface Props {
  open: boolean;
  service: ServiceIdentifier;
  /** The currency this service already quotes in, when it has one. */
  defaultCurrency?: string;
  onClose: () => void;
}

/**
 * A fixed cost price is a single rate for the service, uploaded through the
 * same endpoint as a sliding scale with `kind: 'Fixed'`.
 */
export default function FixedPriceDialog({ open, service, defaultCurrency = '', onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [currency, setCurrency] = useState<string>(defaultCurrency);
  const [price, setPrice] = useState<string>('');

  const uploadCostPrices = useUploadCostPrices(service);

  const parsedPrice = Number(price);
  const isPriceValid = price.trim() !== '' && Number.isFinite(parsedPrice) && parsedPrice >= 0;
  const canSave = !!currency.trim() && isPriceValid && !uploadCostPrices.isPending;

  const handleSave = useCallback(async () => {
    if (!canSave) {
      return;
    }

    try {
      await uploadCostPrices.mutateAsync({
        kind: 'Fixed',
        pricing: [{ currency: currency.trim().toUpperCase(), price: parsedPrice }],
      });

      enqueueSnackbar('Fixed cost price saved', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(`Could not save fixed cost price: ${error?.message ?? 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  }, [canSave, currency, enqueueSnackbar, onClose, parsedPrice, uploadCostPrices]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set a fixed cost price</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            A fixed price replaces the sliding scale for this service: the same rate applies regardless of how many pax
            are booked.
          </Typography>

          <TextField
            label="Currency"
            value={currency}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            required
            error={!currency.trim()}
            helperText={
              currency.trim()
                ? ''
                : "The supplier's own currency, e.g. VND. There is no default: saving in the wrong currency is silent."
            }
            fullWidth
          />

          <TextField
            label="Price"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            type="number"
            required
            error={price.trim() !== '' && !isPriceValid}
            helperText={price.trim() !== '' && !isPriceValid ? 'Enter a positive amount' : ''}
            fullWidth
          />

          {uploadCostPrices.isPending && <LinearProgress />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          Save
        </Button>
        <Button variant="text" onClick={onClose} disabled={uploadCostPrices.isPending}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
